import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, Loader2, AlertCircle, LogOut } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Blocking full-screen modal shown when a logged-in user does NOT have an Alien Passport yet.
 * Refuses to render children (pilot game, wallet, etc.) until the user picks a callsign.
 *
 * The passport handle becomes the pilot display name FOREVER (see pilot_routes.submit_score).
 * That's why we make it explicit and lock-warn.
 */
export default function RequiredPassportModal({ user, onCreated }) {
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasPassport, setHasPassport] = useState(null);   // null=loading, true, false
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const check = useCallback(async () => {
    if (!user) { setChecking(false); setHasPassport(null); return; }
    setChecking(true);
    try {
      const r = await fetch(`${API}/api/passport/me`, { credentials: "include" });
      const d = await r.json();
      setHasPassport(Boolean(d?.passport));
    } catch (e) {
      console.warn("passport check failed:", e);
      setHasPassport(false);   // fail open — let them create one
    }
    setChecking(false);
  }, [user]);

  useEffect(() => { check(); }, [check]);

  const submit = async (e) => {
    e?.preventDefault();
    const clean = handle.trim();
    if (clean.length < 3) { setErr("Callsign precisa ter no mínimo 3 caracteres"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/passport/create`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ handle: clean }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (r.status === 409 && d?.detail?.passport) {
          setHasPassport(true);
          onCreated?.(d.detail.passport);
          return;
        }
        throw new Error(d?.detail?.message || d?.detail || "Falha ao criar passaporte");
      }
      toast.success(`🛸 Passaporte gerado: ${d.passport.handle}`);
      setHasPassport(true);
      onCreated?.(d.passport);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  // Not logged in OR already has passport → do NOT render the blocker
  if (!user || checking || hasPassport !== false) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" data-testid="required-passport-modal">
      <div className="w-full max-w-md rounded-sm border-2 border-primary/50 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Rocket className="h-8 w-8 text-primary" />
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">// REGISTRO OBRIGATÓRIO</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">SEU PASSAPORTE ALIEN</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground font-mono leading-relaxed">
          Antes de pilotar, você precisa escolher seu <span className="text-primary font-bold">callsign</span> — o nome que vai te identificar em todo o jogo e no ranking global.
        </p>

        <div className="rounded-sm border border-amber-400/40 bg-amber-500/5 p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
          <p className="text-xs font-mono text-amber-200 leading-relaxed">
            <span className="font-bold text-amber-300">ATENÇÃO:</span> Esse nome é <span className="text-amber-300 font-bold">DEFINITIVO</span> — depois de criado, não pode ser alterado. Escolhe com carinho.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-muted-foreground">CALLSIGN</label>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value.slice(0, 14).replace(/[^A-Za-z0-9 _-]/g, "").toUpperCase())}
              placeholder="Ex: STARLORD9"
              maxLength={14}
              autoFocus
              data-testid="required-passport-input"
              className="mt-2 w-full rounded-sm border-2 border-primary/40 bg-black/60 px-3 py-3 font-mono text-lg text-primary placeholder-muted-foreground/60 focus:outline-none focus:border-primary tracking-widest"
            />
            <p className="mt-1 font-mono text-[9px] text-muted-foreground tracking-widest">
              3-14 chars · A-Z 0-9 _ - · será convertido pra maiúsculas
            </p>
          </div>

          {err && (
            <div className="rounded-sm border border-red-500/50 bg-red-500/10 p-2 text-xs text-red-300 font-mono">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || handle.trim().length < 3}
            data-testid="required-passport-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 font-mono tracking-widest text-xs px-5 py-3"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-3.5 w-3.5" /> GERAR PASSAPORTE</>}
          </button>
        </form>

        <button
          onClick={() => nav("/")}
          data-testid="required-passport-back"
          className="w-full text-center font-mono text-[10px] tracking-widest text-muted-foreground hover:text-primary flex items-center justify-center gap-1 mt-2"
        >
          <LogOut className="h-3 w-3" /> VOLTAR AO SITE
        </button>
      </div>
    </div>
  );
}
